// The one projects table — filter row + the SHARED table pattern, used by
// /projects (ProjectsPageMui) and the standup Projects tab. Row → /projects/:id,
// so both surfaces land on the SAME project page (the standup tab used to open
// its own inline document view, which is the divergence this component removes).
// The leading star escalates a project into the standup focus list.
//
// D1-c §3.1 moved the body onto `RegisterTable`, so this is now the same
// furniture as the register and the desk: mono 10px labels with no filled header
// band, a 3px left spine coloured by project state, single-line rows, a monogram
// for the owner, and status as a DOT + WORD — never a pill, which the design
// handoff reserves for priority.
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, TextField, InputAdornment, MenuItem, CircularProgress } from "@mui/material";
import type { MouseEvent } from "react";
import type { BrainProject } from "../lib/brain-api";
import { brainTokens } from "../design/brainTheme";
import { SectionCard } from "./SectionCard";
import { type PillTone } from "./StatusPill";
import { Segmented } from "./Segmented";
import { CustomerAvatar } from "./CustomerAvatar";
import { ProgressBar } from "./ProgressBar";
import { Icon } from "./icons";
import {
  AssigneeMonogram,
  RegisterTable,
  RowAction,
  StatusDot,
  type RegisterTableColumn,
  type SpineTone,
} from "./RegisterTable";

const c = brainTokens.color;

const COLUMNS: RegisterTableColumn[] = [
  { key: "focus", label: "", width: "28px", align: "center" },
  { key: "name", label: "Project", width: "minmax(0,1fr)" },
  { key: "actions", label: "Rows", width: "62px", align: "end", collapse: true },
  { key: "customer", label: "Customer", width: "180px", collapse: true },
  { key: "status", label: "Status", width: "132px" },
  { key: "owner", label: "Owner", width: "44px", align: "center", collapse: true },
  { key: "progress", label: "Progress", width: "150px", collapse: true },
];

/** Project state → the 3px left spine. Same vocabulary as the register rows. */
export function projectSpine(status: string): SpineTone {
  const k = (status ?? "").toLowerCase();
  if (k === "blocked") return "danger";
  if (k === "atrisk" || k === "at_risk" || k === "at risk" || k === "paused" || k === "on_hold" || k === "with_client") return "warning";
  if (k === "done" || k === "shipped") return "success";
  if (k === "planning" || k === "planned" || k === "draft" || k === "archived") return "neutral";
  return "teal";
}

export function projectStatusMeta(s: string): { word: string; color: string; tone: PillTone } {
  const k = (s ?? "").toLowerCase();
  if (k === "blocked") return { word: "Blocked", color: c.dangerText, tone: "danger" };
  if (k === "atrisk" || k === "at_risk" || k === "at risk") return { word: "At risk", color: c.warnText2, tone: "warning" };
  if (k === "done" || k === "shipped") return { word: "Delivered", color: c.successText, tone: "success" };
  if (k === "on_hold" || k === "paused") return { word: "On hold", color: c.warnText2, tone: "warning" };
  if (k === "draft") return { word: "Draft", color: c.muted2, tone: "neutral" };
  if (k === "planning" || k === "planned") return { word: "Planning", color: c.muted2, tone: "neutral" };
  if (k === "with_client") return { word: "With client", color: c.warnText2, tone: "warning" };
  if (k === "archived") return { word: "Archived", color: c.muted2, tone: "neutral" };
  return { word: "In progress", color: c.teal, tone: "teal" };
}

export type ProjectSeg = "all" | "in_progress" | "atrisk" | "blocked" | "shipped";
export function matchProjectSeg(status: string, seg: ProjectSeg): boolean {
  if (seg === "all") return true;
  const k = (status ?? "").toLowerCase();
  if (seg === "in_progress") return k === "active" || k === "in_progress";
  if (seg === "atrisk") return k === "atrisk" || k === "at_risk" || k === "at risk";
  if (seg === "blocked") return k === "blocked";
  if (seg === "shipped") return k === "done" || k === "shipped";
  return true;
}

const customerOf = (p: BrainProject) => p.customerName ?? p.customerId ?? "";

export function ProjectsTable({
  projects,
  loading,
  onToggleFocus,
  canFocus = false,
  hideFocused = false,
  title,
}: {
  projects: BrainProject[];
  loading?: boolean;
  /** Star click. Omit (or canFocus=false) to render the star as a read-only marker. */
  onToggleFocus?: (projectId: string, next: boolean) => void;
  canFocus?: boolean;
  /** Standup tab: focused projects already have their own cards above the table. */
  hideFocused?: boolean;
  title?: string;
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [seg, setSeg] = useState<ProjectSeg>("all");
  const [customerSel, setCustomerSel] = useState("");
  const [ownerSel, setOwnerSel] = useState("");
  const [focusOnly, setFocusOnly] = useState(false);

  const customers = useMemo(() => [...new Set(projects.map(customerOf).filter(Boolean))].sort(), [projects]);
  const owners = useMemo(() => [...new Set(projects.map((p) => p.owner ?? "").filter(Boolean))].sort(), [projects]);

  const needle = q.trim().toLowerCase();
  const sorted = useMemo(
    () =>
      projects
        .filter((p) => {
          if (hideFocused && p.isFocus) return false;
          if (focusOnly && !p.isFocus) return false;
          if (!matchProjectSeg(p.status, seg)) return false;
          if (customerSel && customerOf(p) !== customerSel) return false;
          if (ownerSel && (p.owner ?? "") !== ownerSel) return false;
          if (
            needle &&
            ![p.name, p.projectId, customerOf(p), p.owner ?? "", p.description ?? ""].some((s) => s.toLowerCase().includes(needle))
          )
            return false;
          return true;
        })
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
    [projects, hideFocused, focusOnly, seg, customerSel, ownerSel, needle],
  );

  return (
    <SectionCard padding={0}>
      {title && (
        <Box
          sx={{
            p: "20px 23px 0",
            fontSize: 16,
            fontWeight: 700,
            color: c.ink,
            fontFamily: brainTokens.font.sans,
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            "& .ti": { fontSize: 18, color: c.muted },
          }}
        >
          <i className="ti ti-list-numbers" aria-hidden />
          {title}
        </Box>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", p: "16px 20px", flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="Search projects…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search projects"
          sx={{ flex: "0 0 240px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Box sx={{ color: c.faint2, display: "flex" }}>
                  <Icon name="search" size={16} stroke={1.8} />
                </Box>
              </InputAdornment>
            ),
          }}
        />
        <Segmented
          value={seg}
          onChange={setSeg}
          options={[
            { key: "all", label: "All" },
            { key: "in_progress", label: "In progress" },
            { key: "atrisk", label: "At risk" },
            { key: "blocked", label: "Blocked" },
            { key: "shipped", label: "Shipped" },
          ]}
        />
        <TextField select size="small" value={customerSel} onChange={(e) => setCustomerSel(e.target.value)} aria-label="Customer" sx={{ minWidth: 160 }}>
          <MenuItem value="">All customers</MenuItem>
          {customers.map((cu) => (
            <MenuItem key={cu} value={cu}>{cu}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" value={ownerSel} onChange={(e) => setOwnerSel(e.target.value)} aria-label="Owner" sx={{ minWidth: 140 }}>
          <MenuItem value="">All owners</MenuItem>
          {owners.map((o) => (
            <MenuItem key={o} value={o}>{o}</MenuItem>
          ))}
        </TextField>
        {!hideFocused && (
          <Box
            component="button"
            onClick={() => setFocusOnly((v) => !v)}
            sx={{
              display: "inline-flex", alignItems: "center", gap: "6px", padding: "7px 12px",
              borderRadius: "8px", fontSize: 13, cursor: "pointer", fontFamily: brainTokens.font.sans,
              border: `1px solid ${focusOnly ? c.teal : c.border}`,
              background: focusOnly ? c.tealTint : c.white,
              color: focusOnly ? c.tealInk : c.muted,
            }}
          >
            <Icon name="star" size={13} fill={focusOnly ? "currentColor" : "none"} />
            Focus only
          </Box>
        )}
        <Box sx={{ ml: "auto", fontFamily: brainTokens.font.mono, fontSize: 12, color: c.faint2 }}>
          {sorted.length} / {projects.length}
        </Box>
      </Box>

      <Box sx={{ borderTop: `1px solid ${c.borderLight}`, pt: "10px" }}>
        {loading ? (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <CircularProgress size={22} sx={{ color: c.teal }} />
          </Box>
        ) : (
          <RegisterTable
            columns={COLUMNS}
            empty="No projects match the current filter."
            rows={sorted.map((p) => {
              const m = projectStatusMeta(p.status);
              const pct = typeof p.percentComplete === "number" ? p.percentComplete : null;
              const starrable = canFocus && !!onToggleFocus;
              return {
                id: p.projectId,
                spine: projectSpine(p.status),
                title: `${p.name} — ${m.word}`,
                onClick: () => navigate(`/projects/${encodeURIComponent(p.projectId)}`),
                cells: {
                  focus: (
                    <RowAction>
                      <Box
                        component={starrable ? "button" : "span"}
                        type={starrable ? "button" : undefined}
                        title={
                          starrable
                            ? p.isFocus
                              ? "Remove from standup focus"
                              : "Escalate into standup focus"
                            : p.isFocus
                              ? "Focus project"
                              : undefined
                        }
                        aria-label={
                          starrable
                            ? p.isFocus
                              ? `Remove ${p.name} from focus`
                              : `Escalate ${p.name} into focus`
                            : undefined
                        }
                        onClick={
                          starrable
                            ? (e: MouseEvent) => {
                                e.stopPropagation();
                                onToggleFocus!(p.projectId, !p.isFocus);
                              }
                            : undefined
                        }
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "none",
                          border: "none",
                          padding: 0,
                          lineHeight: 0,
                          cursor: starrable ? "pointer" : "default",
                          color: p.isFocus ? c.tealInk : c.placeholder,
                          "&:hover": starrable ? { color: c.teal } : undefined,
                        }}
                      >
                        <Icon name="star" size={16} fill={p.isFocus ? "currentColor" : "none"} />
                      </Box>
                    </RowAction>
                  ),
                  name: (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 500,
                        color: c.ink,
                        letterSpacing: "-0.01em",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                      }}
                    >
                      {p.name}
                    </Box>
                  ),
                  actions: (
                    <Box
                      component="span"
                      title={`${p.openActions ?? 0} open register rows`}
                      sx={{ fontFamily: brainTokens.font.mono, fontSize: 12, color: c.muted2 }}
                    >
                      {p.openActions ?? 0}
                    </Box>
                  ),
                  customer: p.customerId ? (
                    <RowAction>
                      <Box
                        component="a"
                        href={`/customers/${encodeURIComponent(p.customerId)}`}
                        onClick={(e: MouseEvent) => {
                          e.preventDefault();
                          navigate(`/customers/${encodeURIComponent(p.customerId!)}`);
                        }}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          color: c.tealInk,
                          textDecoration: "none",
                          minWidth: 0,
                          fontSize: 13,
                        }}
                      >
                        <CustomerAvatar name={p.customerName ?? p.customerId} size={22} />
                        <Box
                          component="span"
                          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          {p.customerName ?? p.customerId}
                        </Box>
                      </Box>
                    </RowAction>
                  ) : (
                    <Box component="span" sx={{ color: c.muted2, fontSize: 13 }}>
                      Internal
                    </Box>
                  ),
                  // Dot + word, never a pill — the pill is reserved for priority.
                  status: <StatusDot label={m.word} tone={m.tone} />,
                  owner: <AssigneeMonogram name={p.owner ?? null} />,
                  progress: (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
                      <Box sx={{ flex: 1, minWidth: 50 }}>
                        <ProgressBar value={pct ?? 0} />
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontFamily: brainTokens.font.mono,
                          fontSize: 12,
                          color: c.muted2,
                          width: 34,
                          textAlign: "right",
                        }}
                      >
                        {pct != null ? `${pct}%` : "—"}
                      </Box>
                    </Box>
                  ),
                },
              };
            })}
          />
        )}
      </Box>
    </SectionCard>
  );
}
